import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Theme context
const ThemeContext = createContext();

// Theme reducer
const themeReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LIGHT':
      return 'light';
    case 'SET_DARK':
      return 'dark';
    case 'TOGGLE':
      return state === 'light' ? 'dark' : 'light';
    default:
      return state;
  }
};

// ThemeProvider component
export const ThemeProvider = ({ children }) => {
  // Get initial theme from localStorage or system preference
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    
    if (savedTheme === 'light' || savedTheme === 'dark') {
      return savedTheme;
    }
    
    // Check system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  };

  const [theme, dispatch] = useReducer(themeReducer, getInitialTheme());

  // Apply theme to document when it changes
  useEffect(() => {
    const root = window.document.documentElement;
    const body = window.document.body;
    
    // Remove both theme classes first
    root.classList.remove('light', 'dark');
    body.classList.remove('light', 'dark');
    
    // Add current theme class
    root.classList.add(theme);
    body.classList.add(theme);
    
    // Update data-theme attribute for CSS variables
    root.setAttribute('data-theme', theme);
    
    // Save to localStorage
    localStorage.setItem('theme', theme);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        theme === 'dark' ? '#1a1a1a' : '#ffffff'
      );
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only update if user hasn't manually set a theme
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        dispatch({ type: e.matches ? 'SET_DARK' : 'SET_LIGHT' });
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } 
    // Older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Toggle theme
  const toggleTheme = () => {
    dispatch({ type: 'TOGGLE' });
  };

  // Set light theme
  const setLightTheme = () => {
    dispatch({ type: 'SET_LIGHT' });
  };

  // Set dark theme
  const setDarkTheme = () => {
    dispatch({ type: 'SET_DARK' });
  };

  // Set theme by name
  const setTheme = (themeName) => {
    if (themeName === 'light') {
      setLightTheme();
    } else if (themeName === 'dark') {
      setDarkTheme();
    }
  };

  // Check if dark theme is active
  const isDark = theme === 'dark';
  
  // Check if light theme is active
  const isLight = theme === 'light';

  const value = {
    theme,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    setTheme,
    isDark,
    isLight
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

export default ThemeContext;